import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import {
  Dialog,
  Button,
  Typography,
  Box,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import DropdownComponent from '../DropDown/DropDownComponent';
import TextFieldComponent from '../TextField/TextFieldComponent';
import ButtonGroupComponent from '../ButtonGroup/ButtonGroupComponent';
import CardButtonGroupComponent from '../CardButtonGroup/CardButtonGroupComponent';
import PillDateSelectComponent from '../PillDateSelect/PillDateSelectComponent';
import SegmentedScaleComponent from '../SegmentedScale/SegmentedScaleComponent';
import OtpInputComponent from '../OtpInput/OtpInputComponent';
import { toSentenceCase } from '../../utils/casing';

const hasOtpInputQuestion = (questions) =>
  Array.isArray(questions) &&
  questions.some((group) =>
    Object.values(group).some((question) => question?.type === 'otp-input')
  );

const renderAlignedMaskedText = (text) => {
  if (typeof text !== 'string' || !text.includes('•')) return text;

  return text.split(/(•+)/g).map((part, index) => {
    if (!part) return null;

    if (/^•+$/.test(part)) {
      return (
        <Box
          component="span"
          key={`masked-${index}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            position: 'relative',
            top: '-0.14em',
            lineHeight: 1,
          }}
        >
          {part}
        </Box>
      );
    }

    return part;
  });
};

/** ---------- Countdown-enabled Button (ref-based, no React state) ---------- */
const CountdownButton = ({
  btn,
  isMobile,
  isCompactMobile,
  onClick,
  busy = false,
}) => {
  const countdownEnabled = Boolean(btn?.countdown?.enabled);
  const startSeconds = Math.max(0, Number(btn?.countdown?.seconds ?? 0));

  const numberRef = useRef(null);
  const intervalRef = useRef(null);
  const counterRef = useRef(startSeconds);
  const firedRef = useRef(false);
  const mountedRef = useRef(false);

  // animate number text: fade out -> swap -> scale+fade in
  const animateNumber = (nextText) => {
    const el = numberRef.current;
    if (!el) return;
    const tl = gsap.timeline();
    tl.to(el, { opacity: 0, duration: 0.12, ease: 'power1.out' })
      .set(el, { scale: 0.5 })
      .call(() => {
        // direct DOM update (no TextPlugin needed)
        el.textContent = nextText;
      })
      .to(el, { opacity: 1, scale: 1, duration: 0.18, ease: 'power2.out' });
  };

  useEffect(() => {
    mountedRef.current = true;

    // Initialize label if enabled
    if (countdownEnabled && numberRef.current) {
      numberRef.current.textContent = `${startSeconds}s`;
      gsap.set(numberRef.current, { opacity: 1, scale: 1 });
    }

    // If countdown requested but seconds <= 0, fire immediately (after a tiny delay to render the 0s)
    if (countdownEnabled && startSeconds <= 0 && !firedRef.current) {
      firedRef.current = true;
      const t = setTimeout(() => onClick?.(), 100);
      return () => clearTimeout(t);
    }

    // Start ticking
    if (countdownEnabled && startSeconds > 0) {
      intervalRef.current = setInterval(() => {
        // decrement
        counterRef.current = counterRef.current - 1;
        const next = counterRef.current;

        // show next number (clamped)
        const label = `${Math.max(next, 0)}s`;
        animateNumber(label);

        // fire click at 0 (once)
        if (next === 0 && !firedRef.current) {
          firedRef.current = true;
          setTimeout(() => onClick?.(), 150);
        }

        // stop after passing below zero
        if (next <= 0) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, 1000);
    }

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdownEnabled, startSeconds]);

  // User clicked manually: stop timer and fire immediately (only once)
  const handleClick = () => {
    if (busy) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!firedRef.current) {
      firedRef.current = true;
      onClick?.();
    }
  };

  // styling identical to your existing logic
  let bg = '#C7CA00';
  let textColor = '#1a2027';
  let border = '1px solid #C7CA00';

  if (btn.type === 'primary-pink') {
    bg = '#ff0074';
    textColor = '#ffffff';
    border = '1px solid #ff0074';
  }

  if (btn.type === 'secondary-gray') {
    return (
      <Button
        key={btn.id}
        className="stagger-in"
        onClick={handleClick}
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '0.625rem',
          color: '#b3b3b3',
          textDecoration: 'underline',
          textDecorationSkipInk: 'none',
          textUnderlineOffset: '2px',
          background: 'none',
          border: 'none',
          padding: 0,
          height: 'auto',
          textTransform: 'none',
          boxShadow: 'none',
          cursor: 'pointer',
          ...(btn.mt != null ? { mt: btn.mt } : {}),
        }}
      >
        {toSentenceCase(btn.text)}
      </Button>
    );
  }

  if (btn.type === 'third-gray') {
    return (
      <Button
        key={btn.id}
        className="stagger-in"
        onClick={handleClick}
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: btn.bold ? 700 : 400,
          fontSize: '0.75rem',
          lineHeight: '1.25rem',
          letterSpacing: '-0.002em',
          verticalAlign: 'middle',
          color: '#A2ACB1',
          textDecoration: 'none',
          background: 'none',
          border: 'none',
          padding: 0,
          height: 'auto',
          textTransform: 'none',
          boxShadow: 'none',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'transparent',
            color: '#A2ACB1',
            fontWeight: 700,
            textDecoration: 'underline',
            textDecorationSkipInk: 'none',
            textUnderlineOffset: '2px',
          },
          ...(btn.mt != null ? { mt: btn.mt } : {}),
        }}
      >
        {toSentenceCase(btn.text)}
      </Button>
    );
  }

  return (
    <Button
      key={btn.id}
      className="stagger-in"
      onClick={handleClick}
      disabled={busy}
      sx={{
        px: isCompactMobile ? '16px' : isMobile ? '36px' : '20px',
        width: isCompactMobile ? '100%' : 'auto',
        maxWidth: '100%',
        minWidth: isCompactMobile ? 0 : 'auto',
        minHeight: isCompactMobile ? '40px' : '44px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: isCompactMobile ? '0.875rem' : isMobile ? '1rem' : '1.25rem',
        lineHeight: 1.15,
        whiteSpace: isCompactMobile ? 'normal' : 'nowrap',
        textAlign: 'center',
        borderRadius: '18px',
        textTransform: 'none',
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        mb: '24px',
        backgroundColor: bg,
        color: textColor,
        border,
        '&:hover': {
          backgroundColor: bg,
        },
        '&.Mui-disabled': {
          backgroundColor: bg,
          color: textColor,
          border,
          opacity: 0.85,
        },
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: countdownEnabled ? '10px' : 0,
        boxSizing: 'border-box',
      }}
    >
      {busy ? (
        <CircularProgress size={20} thickness={5} sx={{ color: textColor }} />
      ) : (
        <>
          {toSentenceCase(btn.text)}
          {countdownEnabled && (
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                fontSize: isCompactMobile
                  ? '0.875rem'
                  : isMobile
                    ? '1rem'
                    : '1.25rem',
                color: textColor,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
              }}
              ref={numberRef}
            >
              {/* will be set in effect */}
            </Box>
          )}
        </>
      )}
    </Button>
  );
};
/** ---------- /Countdown-enabled Button ---------- */

const ModalContent = React.memo(function ModalContent({
  title,
  subtitle,
  content,
  text,
  questions,
  buttons,
  formValues,
  setFormValues,
  onAction,
  onComplete,
  onClose,
  isMobile,
  isCompactMobile = false,
  isInformation,
  isOtpFlow = false,
  loading = false,
  otpError = '',
  otpResetCounter = 0,
}) {
  const handleChange = (key, value) =>
    setFormValues((prev) => ({ ...prev, [key]: value }));

  const renderQuestion = (key, question) => {
    if (question.showIf) {
      const dependencyValue = formValues?.[question.showIf.key];
      if (question.showIf.equals && dependencyValue !== question.showIf.equals)
        return null;
      if (
        question.showIf.notEquals &&
        dependencyValue === question.showIf.notEquals
      )
        return null;
    }

    const autoAdvanceTypes = [
      'button-group',
      'card-button-group',
      'segmented-scale',
      'otp-input',
    ];
    const shouldAutoAdvance =
      autoAdvanceTypes.includes(question.type) && !question.disableAutoNext;

    const props = {
      name: key,
      question: { ...question },
      value: formValues?.[key] || '',
      isMobile,
      onChange: handleChange,
      loading,
      ...(shouldAutoAdvance && {
        onNext: () => {
          onAction?.('auto-next', formValues);
          onClose();
        },
      }),
    };

    switch (question.type) {
      case 'text-field':
        return <TextFieldComponent key={key} {...props} isPopUpModal />;
      case 'button-group':
        return <ButtonGroupComponent key={key} {...props} />;
      case 'dropdown':
        return <DropdownComponent key={key} {...props} isPopUpModal />;
      case 'card-button-group':
        return <CardButtonGroupComponent key={key} {...props} />;
      case 'pill-date-select':
        return <PillDateSelectComponent key={key} {...props} />;
      case 'segmented-scale':
        return <SegmentedScaleComponent key={key} {...props} />;
      case 'otp-input':
        return (
          <OtpInputComponent
            key={key}
            {...props}
            length={question.length || 4}
            loading={props.loading}
            errorText={otpError}
            resetCounter={otpResetCounter}
            label="Enter your code:"
            autoSubmit={(fullOtp) => {
              const newFormValues = { ...formValues, [key]: fullOtp };
              // Tell parent the OTP is ready; parent will trigger API + set busy
              onAction?.('submit-otp', newFormValues);
              onComplete?.(newFormValues); // optional: keep if you want this signal
              // DO NOT close here; wait for parent success
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {title && (
        <Typography
          className="stagger-in"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: isMobile
              ? isOtpFlow
                ? '1.125rem'
                : '1.25rem'
              : '1.5rem',
            mb: subtitle
              ? '20px'
              : content
                ? isOtpFlow
                  ? '12px'
                  : '16px'
                : '24px',
            color: '#1a2027',
            whiteSpace: 'pre-line',
          }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography
          className="stagger-in"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.002em',
            textAlign: 'center',
            color: '#162F3B',
            mb: isOtpFlow ? '18px' : '24px',
            width: '378px',
            maxWidth: '100%',
            whiteSpace: 'pre-line',
          }}
        >
          {subtitle}
        </Typography>
      )}
      {content && (
        <Typography
          className="stagger-in"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: isMobile ? '0.75rem' : '1rem',
            mb: '24px',
            color: isInformation ? '#162F3B' : '#4a4f55',
            width: '378px',
            maxWidth: '100%',
            whiteSpace: 'pre-line',
          }}
        >
          {renderAlignedMaskedText(content)}
        </Typography>
      )}

      {text && (
        <Typography
          className="stagger-in"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: isMobile ? '0.6875rem' : '1rem',
            lineHeight: '1.25rem',
            mb: isOtpFlow ? '18px' : '24px',
            color: '#162F3B',
            width: '414px',
            maxWidth: '100%',
            whiteSpace: 'pre-line',
            textAlign: 'left',
          }}
        >
          {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
            if (/^\*\*[^*]+\*\*$/.test(part)) {
              return (
                <strong key={i} style={{ fontWeight: 700 }}>
                  {part.slice(2, -2)}
                </strong>
              );
            }
            return part;
          })}
        </Typography>
      )}

      {Array.isArray(questions) && (
        <Box
          sx={{
            mb: buttons.length > 0 ? (isOtpFlow ? '20px' : '28px') : '24px',
            width: '100%',
          }}
        >
          {questions.map((questionGroup, groupIndex) => {
            const layout = questionGroup.layout === 'row' ? 'row' : 'column';
            return (
              <Box
                key={groupIndex}
                className="stagger-in"
                sx={{
                  display: 'flex',
                  flexDirection: layout,
                  flexWrap: layout === 'row' ? 'wrap' : 'nowrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap:
                    layout === 'row'
                      ? isOtpFlow && isMobile
                        ? '12px'
                        : '16px'
                      : isOtpFlow && isMobile
                        ? '18px'
                        : '24px',
                  width: '100%',
                }}
              >
                {Object.entries(questionGroup)
                  .filter(([k]) => k !== 'layout')
                  .map(([key, question]) => renderQuestion(key, question))}
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
        }}
      >
        {buttons
          .sort((a, b) => a.index - b.index)
          .map((btn) => {
            const clickHandler = () => {
              if (btn.link) {
                // Prefer your app's navigation function:
                if (typeof window !== 'undefined') {
                  window.location.href = btn.link; // simplest fallback
                }
                onClose?.();
                return;
              }

              if (btn.completes) {
                onComplete?.(formValues);
              } else {
                onAction?.(btn.id, formValues);
              }
            };
            return (
              <CountdownButton
                key={btn.id}
                btn={btn}
                isMobile={isMobile}
                isCompactMobile={isCompactMobile}
                onClick={clickHandler}
                busy={btn.id === 'send-otp' && loading}
              />
            );
          })}
      </Box>
    </>
  );
});

const PopupModalComponent = ({
  data,
  open,
  onClose,
  onAction,
  onComplete,
  isMobile,
  loading = false,
  otpError = '',
  otpResetCounter = 0,
}) => {
  const { title, content, text, buttons = [], questions } = data;
  const subtitle = data.subtitle;
  // 'otp-confirm-send' (ask-before-sending) and 'otp-input' (enter the code)
  // are two steps of the same verification flow — treat both as "OTP flow"
  // so the avatar/spacing match and the dialog doesn't visibly resize
  // between them.
  const isOtpFlow =
    hasOtpInputQuestion(questions) ||
    data.type === 'otp-input' ||
    data.type === 'otp-confirm-send';
  const isCompactMobile = useMediaQuery('(max-width: 380px)');
  const [formValues, setFormValues] = useState({});
  const [internalOpen, setInternalOpen] = useState(false);
  const paperRef = useRef(null);

  const HippoAvatar = isOtpFlow
    ? 'https://assets.askhippo.co.za/avatars/v2/Wave-v2.gif'
    : 'https://assets.askhippo.co.za/avatars/v2/Wave-v2.gif';

  const handleModalAction = (actionId) => {
    // Only report the action; parent decides if/when to close
    if (onAction) return onAction(actionId, formValues);
    if (onComplete) return onComplete(formValues);
  };

  useEffect(() => {
    if (open) {
      setInternalOpen(true);
      setTimeout(() => {
        const el = paperRef.current;
        if (!el) return;

        const staggerElements = el.querySelectorAll('.stagger-in');
        gsap.set(staggerElements, { opacity: 0, y: 30 });

        gsap
          .timeline()
          .fromTo(
            el,
            { scale: 0.5, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: 1.2,
              ease: 'elastic.out(1.4, 0.75)',
            }
          )
          .to(
            staggerElements,
            {
              y: 0,
              opacity: 1,
              duration: 0.5,
              ease: 'power2.out',
              stagger: 0.1,
            },
            '<0.2'
          );
      }, 50);
    } else if (internalOpen) {
      const el = paperRef.current;
      if (!el) return;

      gsap
        .timeline({
          onComplete: () => {
            setInternalOpen(false);
            setFormValues({});
          },
        })
        .to(el, {
          opacity: 0,
          duration: 0.1,
          ease: 'power2.in',
        });
    }
  }, [open, internalOpen]);

  useEffect(() => {
    if (open && questions) {
      const initialValues = {};
      questions.forEach((group) => {
        Object.entries(group).forEach(([key, question]) => {
          if (key !== 'layout' && question?.value !== undefined) {
            initialValues[key] = question.value;
          }
        });
      });
      setFormValues(initialValues);
    }
  }, [open, questions]);

  useEffect(() => {
    if (!open || !questions) return;

    // Check if any fields became visible due to formValues changes
    // and apply their default values if they're empty
    const updatedValues = { ...formValues };
    let hasChanges = false;

    questions.forEach((group) => {
      Object.entries(group).forEach(([key, question]) => {
        if (key === 'layout') return;

        // Check if this field should be visible based on showIf
        let shouldBeVisible = true;
        if (question.showIf) {
          const dependencyValue = formValues?.[question.showIf.key];
          if (
            question.showIf.equals &&
            dependencyValue !== question.showIf.equals
          )
            shouldBeVisible = false;
          if (
            question.showIf.notEquals &&
            dependencyValue === question.showIf.notEquals
          )
            shouldBeVisible = false;
        }

        // If visible and empty, apply default value (first option if available, else question.value)
        if (shouldBeVisible && !updatedValues[key]) {
          const defaultValue = question.options?.[0] || question.value;
          if (defaultValue) {
            updatedValues[key] = defaultValue;
            hasChanges = true;
          }
        }
      });
    });

    if (hasChanges) {
      setFormValues(updatedValues);
    }
  }, [formValues, questions, open]);

  return (
    internalOpen && (
      <Dialog
        open={internalOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            onClose();
          }
        }}
        TransitionProps={{ mountOnEnter: true, unmountOnExit: false }}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            ref: paperRef,
            sx: {
              maxWidth: isMobile
                ? isOtpFlow
                  ? 'calc(100vw - 24px)'
                  : 'calc(100% - 84px)'
                : data.type === 'information'
                  ? '478px'
                  : '446px',
              borderRadius: '17px',
              backgroundColor: '#ffffff',
              boxShadow: '0px 4px 32px rgba(16, 24, 40, 0.12)',
              p: 0,
              maxHeight: 'calc(100dvh - 48px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              position: 'absolute',
              inset: 0,
              margin: 'auto',
              height: 'fit-content',
              // Ask-before-sending and enter-the-code are two steps of the
              // same OTP flow — share a min-height so the dialog doesn't
              // visibly grow/shrink when "Send OTP" swaps the content.
              ...(isOtpFlow && {
                minHeight: isMobile ? '560px' : '600px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }),
            },
          },
          root: {
            sx: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          },
          backdrop: {
            sx: {
              backgroundColor: 'rgba(28, 47, 58, 0.34)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: isMobile && isOtpFlow ? 2 : 4,
            py: isMobile && isOtpFlow ? '24px' : '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: '#fff',
            borderRadius: 2,
          }}
        >
          <Box
            className="stagger-in"
            component="img"
            src={HippoAvatar}
            alt="HippoAvatar"
            sx={{
              width: isOtpFlow ? 152 : isMobile ? 72 : 104,
              height: isOtpFlow ? 152 : isMobile ? 72 : 104,
              mb: isOtpFlow ? '24px' : isMobile ? '16px' : '24px',
              boxSizing: 'content-box',
            }}
          />
          <ModalContent
            title={title}
            subtitle={subtitle}
            content={content}
            questions={questions}
            text={text}
            isInformation={data.type === 'information'}
            isOtpFlow={isOtpFlow}
            buttons={buttons.map((btn) => ({
              ...btn,
              onClick: () => handleModalAction(btn.id),
            }))}
            formValues={formValues}
            setFormValues={setFormValues}
            onAction={handleModalAction}
            onComplete={onComplete}
            onClose={onClose}
            isMobile={isMobile}
            isCompactMobile={isCompactMobile}
            loading={loading}
            otpError={otpError}
            otpResetCounter={otpResetCounter}
          />
        </Box>
      </Dialog>
    )
  );
};

export default React.memo(PopupModalComponent);
